import {
  useEffect, useMemo, ChangeEvent, FocusEvent,
} from 'react'

import { digObject, deepSetObject, isValidRequiredEntity } from '@domain-group/javascript-utils'

import useSetState from '@hooks/useSetState'

type RequiredField = {
  key: string,
}

export type Errors = {
  [key: string]: string,
}

export type Entity = {
  id: number | null,
  [key: string]: any,
} | {}

export type EntityState = {
  id: number | null,
  [key: string]: any,
}

export type SetEntityState = (fields: { [key: string]: any }) => void

export type UseFormDefaultState = {
  [key: string]: any,
}

export type UseFormOptions = {
  entity?: Entity,
  onBlurFn?: (entityState: EntityState) => void,
  requiredFields?: RequiredField[],
  validateOn?: 'blur' | 'change',
}

export type UseFormPayload = {
  blurFields: string[],
  dirtyFields: string[],
  entityState: any,
  errors: Errors,
  onBlurEntityState: (field: string) => void,
  resetDefaultState: () => void,
  saveEnabled: boolean,
  setEntityState: SetEntityState,
  handlers: {
    onBlur: (event: FocusEvent<HTMLInputElement>) => void,
    onChange: (event: ChangeEvent<HTMLInputElement>) => void,
  },
}

export type UseFormParams = {
  defaultState: UseFormDefaultState,
  options?: UseFormOptions,
  deps?: any[],
}

export type EntityStateHook = [
  entityState: EntityState,
  setEntityState: SetEntityState,
]

type FormState = {
  [key: string]: string[],
}

type SetFormState = (fields: { [key: string]: any }) => void

type FormStateHook = [
  formState: FormState,
  setFormState: SetFormState,
]

const isObject = (value: any) => typeof value === 'object' && !Array.isArray(value) && value !== null

const setupState = (entity: Entity, defaultModelState: UseFormDefaultState, setEntityState: SetEntityState) => {
  const state = Object.keys(defaultModelState).reduce((acc: { [key: keyof UseFormDefaultState]: any }, key: keyof UseFormDefaultState) => {
    const defaultValue = defaultModelState[key]
    const newValue = (entity as any)[key]

    const defaultIsObject = isObject(defaultValue)
    const newIsObject = isObject(newValue)

    const updatedValue = defaultIsObject && newIsObject
      ? { ...JSON.parse(JSON.stringify(defaultValue)), ...JSON.parse(JSON.stringify(newValue)) }
      : newValue

    acc[key] = updatedValue === undefined || updatedValue === null ? defaultValue : updatedValue

    return acc
  }, {})

  setEntityState({ ...state })
}

const setupDirtyFields = (entity: Entity, defaultModelState: UseFormDefaultState, setFormState: SetFormState) => {
  const dirtyFields = Object.keys(entity).filter(key => (entity as any)[key] !== defaultModelState[key])

  setFormState({ dirtyFields })
}

const parseValueByType = (e: ChangeEvent<HTMLInputElement>) => {
  const { value, type, dataset } = e.target
  const valueType = digObject(dataset, 'valueType')

  if (value && (type === 'number' || valueType === 'number')) return Number(value)
  if (value && valueType === 'integer') return Number.isNaN(parseInt(value, 10)) ? '' : parseInt(value, 10)
  if (valueType === 'boolean') return value === 'true'

  return value
}

const handleOnBlur = (
  e: FocusEvent<HTMLInputElement>,
  formStateHook: FormStateHook,
  entityStateHook: any,
  onBlurFn: ((entityState: EntityState) => void) | undefined,
) => {
  const [entityState] = entityStateHook
  const [formState, setFormState] = formStateHook
  const { name } = e.target

  if (name){
    const { blurFields } = formState
    setFormState({ blurFields: [...blurFields, name] })

    // Use a custom onBlurFn if provided
    if (onBlurFn) onBlurFn(entityState)
  }
}

const updateDirtyFields = (fields: string[], formStateHook: FormStateHook) => {
  const [formState, setFormState] = formStateHook
  const { dirtyFields } = formState
  const updatedDirtyFields = [...dirtyFields, ...fields]
  setFormState({ dirtyFields: updatedDirtyFields })
}

type ValidateOn = 'blur' | 'change' | undefined

const updateDirtyFieldsForChange = (
  validateFormOn: ValidateOn,
  validateFieldOn: ValidateOn,
  name: string,
  formStateHook: FormStateHook,
) => {
  if (
    (validateFormOn === 'change' && validateFieldOn !== 'blur')
    || validateFieldOn === 'change'
  ){
    updateDirtyFields([`${name}`], formStateHook)
  }
}

const handleOnChange = (
  e: ChangeEvent<HTMLInputElement>,
  entityStateHook: any,
  formStateHook: FormStateHook,
  options: UseFormOptions,
) => {
  const { validateOn: validateFormOn } = options
  const [entityState, setEntityState] = entityStateHook
  const { name, dataset } = e.target
  const validateFieldOn = digObject(dataset, 'validateFieldOn')

  if (!name) return

  const updatedValue = parseValueByType(e)
  const isObjectField = name.includes('.')

  // Object Field e.g. data.nested
  if (isObjectField){
    const [rootObjectName, ...restKeys] = name.split('.')
    const rootObject = entityState[rootObjectName]
    setEntityState({ [rootObjectName]: deepSetObject(rootObject, [...restKeys].join('.'), updatedValue) })
    updateDirtyFieldsForChange(validateFormOn, validateFieldOn, name, formStateHook)
    return
  }

  // Standard Field
  setEntityState({ [name]: updatedValue })
  updateDirtyFieldsForChange(validateFormOn, validateFieldOn, name, formStateHook)
}

const updateBlurFields = (fields: string[], formStateHook: FormStateHook) => {
  const [formState, setFormState] = formStateHook
  const { blurFields } = formState
  const updatedBlurFields = [...blurFields, ...fields]

  setFormState({ blurFields: updatedBlurFields })
}

const handleSetEntityState = (
  fields: { [key: string]: any },
  setEntityState: SetEntityState,
  formStateHook: FormStateHook,
  options: UseFormOptions,
) => {
  const { validateOn } = options || {}

  setEntityState(fields)

  if (validateOn === 'change'){
    updateDirtyFields(Object.keys(fields), formStateHook)
    return
  }

  updateBlurFields(Object.keys(fields), formStateHook)
}

const handleResetDefaultState = (
  defaultState: UseFormDefaultState,
  defaultFormState: FormState,
  setEntityState: SetEntityState,
  setFormState: SetFormState,
) => {
  setEntityState({ ...defaultState })
  setFormState({ ...defaultFormState })
}

const defaultFormState = {
  blurFields: [],
  dirtyFields: [],
}

function useForm(
  defaultState: UseFormDefaultState,
  options: UseFormOptions = {
    entity: {},
    requiredFields: [],
    validateOn: 'blur',
  },
  deps: any[] = [],
): UseFormPayload {
  const { entity, onBlurFn } = options || {}
  const requiredFields = digObject(options, 'requiredFields', [])

  const entityStateHook = useSetState(defaultState)
  const [entityState, setEntityState] = entityStateHook

  const formStateHook = useSetState(defaultFormState)
  const [formState, setFormState] = formStateHook
  const { blurFields, dirtyFields } = formState

  const { isValid: saveEnabled, errors } = useMemo(() => {
    const fieldsToValidate = [...new Set([...blurFields, ...dirtyFields])]
    const entityValid = isValidRequiredEntity(requiredFields, entityState, fieldsToValidate)
    return entityValid
  }, [blurFields.length, dirtyFields.length, ...deps])

  useEffect(() => {
    if (entity){
      setupState(entity, defaultState, setEntityState)
      setupDirtyFields(entity, defaultState, setFormState)
    }
  }, [...deps])

  return {
    blurFields,
    dirtyFields,
    entityState,
    errors,
    onBlurEntityState: fieldName => updateBlurFields([fieldName], formStateHook),
    resetDefaultState: () => handleResetDefaultState(defaultState, defaultFormState, setEntityState, setFormState),
    saveEnabled,
    setEntityState: fields => handleSetEntityState(fields, setEntityState, formStateHook, options),
    handlers: {
      onBlur: e => handleOnBlur(e, formStateHook, entityStateHook, onBlurFn),
      onChange: e => handleOnChange(e, entityStateHook, formStateHook, options),
    },
  }
}

export default useForm
